/**
 * Time-To-Live (TTL) Session Storage & In-Memory Cache Utility
 * Provides lightweight client-side response caching for frequent API queries.
 */

interface CacheEntry<T> {
  data: T;
  expiry: number; // Timestamp in milliseconds when the cache expires
}

/**
 * Set item in sessionStorage with TTL (in minutes)
 */
export function setSessionCache<T>(key: string, data: T, ttlMinutes: number = 5): void {
  try {
    const expiry = Date.now() + ttlMinutes * 60 * 1000;
    const entry: CacheEntry<T> = { data, expiry };
    sessionStorage.setItem(`app_cache_${key}`, JSON.stringify(entry));
  } catch (err) {
    console.warn(`[Cache] Failed to save key "${key}" to sessionStorage:`, err);
  }
}

/**
 * Get item from sessionStorage if valid and not expired
 */
export function getSessionCache<T>(key: string): T | null {
  try {
    const raw = sessionStorage.getItem(`app_cache_${key}`);
    if (!raw) return null;
    const entry: CacheEntry<T> = JSON.parse(raw);

    // Check expiration
    if (Date.now() > entry.expiry) {
      sessionStorage.removeItem(`app_cache_${key}`);
      return null;
    }
    return entry.data;
  } catch (err) {
    console.warn(`[Cache] Failed to read key "${key}" from sessionStorage:`, err);
    return null;
  }
}

/**
 * Clear specific session cache key or all application session caches
 */
export function clearSessionCache(key?: string): void {
  try {
    if (key) {
      sessionStorage.removeItem(`app_cache_${key}`);
    } else {
      Object.keys(sessionStorage).forEach((k) => {
        if (k.startsWith("app_cache_")) {
          sessionStorage.removeItem(k);
        }
      });
    }
  } catch (err) {
    console.warn("[Cache] Failed to clear session cache:", err);
  }
}
